import { useId, useState, type KeyboardEvent } from 'react'

import { FieldShell } from '@/components/ui/Field'
import { cn } from '@/lib/utils'

export const sameText = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase()

/** Free-text values as removable chips: Enter or a comma adds one. */
export function ChipsInput({
  label,
  values,
  onChange,
  placeholder,
  hint,
  className,
}: {
  label: string
  values: string[]
  onChange: (values: string[]) => void
  placeholder?: string
  hint?: string
  className?: string
}) {
  const id = useId()
  const [text, setText] = useState('')

  const commit = (raw: string) => {
    const additions = raw
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
    if (additions.length) {
      const next = [...values]
      for (const value of additions) {
        if (!next.some((existing) => sameText(existing, value))) next.push(value)
      }
      onChange(next)
    }
    setText('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault()
      commit(text)
    } else if (event.key === 'Backspace' && text === '' && values.length > 0) {
      onChange(values.slice(0, -1))
    }
  }

  return (
    <FieldShell label={label} hint={hint} htmlFor={id} className={className}>
      {/* The box's own focus ring: the input inside it carries the focus. */}
      <div className="flex min-h-11 w-full flex-wrap items-center gap-1.5 rounded-field border border-line bg-surface px-2.5 py-1.5 transition focus-within:border-ink-soft focus-within:ring-2 focus-within:ring-ink/10">
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex max-w-full items-center gap-1 rounded-[6px] bg-surface-muted py-0.5 pl-2 pr-1 text-[13px] text-ink"
          >
            <span className="truncate">{value}</span>
            <button
              type="button"
              onClick={() => onChange(values.filter((existing) => existing !== value))}
              aria-label={`Remove ${value}`}
              className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-line hover:text-ink"
            >
              <svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="m2 2 6 6M8 2 2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          </span>
        ))}
        <input
          id={id}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          // A half-typed value still counts when they move on to the next field.
          onBlur={() => commit(text)}
          placeholder={values.length === 0 ? placeholder : undefined}
          className="min-w-[8rem] flex-1 bg-transparent py-1 text-[14px] text-ink outline-none placeholder:text-ink-faint"
        />
      </div>
    </FieldShell>
  )
}

/** Multi-select as toggle pills. */
export function ChoiceChips({
  label,
  options,
  values,
  onChange,
  hint,
  className,
}: {
  label: string
  options: Array<{ value: string; label: string }>
  values: string[]
  onChange: (values: string[]) => void
  hint?: string
  className?: string
}) {
  const toggle = (value: string) =>
    onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value])

  return (
    <FieldShell label={label} hint={hint} className={className}>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={label}>
        {options.map((option) => {
          const selected = values.includes(option.value)
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={selected}
              onClick={() => toggle(option.value)}
              className={cn(
                'rounded-pill border px-3 py-1.5 text-[13px] font-medium transition-colors',
                selected
                  ? 'border-ink bg-ink text-oncolor'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong hover:text-ink',
              )}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </FieldShell>
  )
}
